<!--
  @component Navbar.MegaMenuLink

  A component for rendering links within a mega menu in the navbar.
  It handles focus, blur, and keyboard navigation events for improved accessibility.

  @prop href - The URL the link points to (required)
  @prop label - The text content and accessible label for the link (required)
  @prop css - Custom CSS styles to apply to the link

  Example:
  <Navbar.MegaMenuLink href="/electronics/laptops" label="Laptops" />
-->

<script lang="ts">
  import { css } from '@agapi/ui/panda/css';
  import { megaMenuStyles } from '../navbar.styles';
  import { getContext } from 'svelte';
  import type { NavbarStore } from '../navbar-store.svelte';
  import type { NavbarItemContext, NavbarMegaMenuLinkProps } from '../navbar.types';
  import { goto } from '$app/navigation';

  /** Props for the Navbar.MegaMenuLink component */
  const { href, label, css: cssProp }: NavbarMegaMenuLinkProps = $props();

  const styles = megaMenuStyles.raw();
  /** Navbar context for managing state */
  const navbarContext = getContext<NavbarStore>('navbar');
  /** Navbar item context for managing item-specific state */
  const navbarItemContext = getContext<NavbarItemContext>('navbar-item');

  /**
   * Handles focus event for the link
   * Clears any existing close timeout and sets a new hover timeout
   */
  const onfocus = () => {
    if (navbarContext.closeTimeoutId) {
      clearTimeout(navbarContext.closeTimeoutId);
      navbarContext.setCloseTimeout(null);
    }

    const timeout = setTimeout(() => {
      navbarContext.setOpenItem(navbarItemContext.id);
    }, navbarItemContext.hoverDelay);
    navbarContext.setHoverTimeout(timeout as unknown as number);
  };

  /**
   * Handles blur event for the link
   * Clears any existing hover timeout and sets a new close timeout
   */
  const onblur = () => {
    if (navbarContext.hoverTimeoutId) {
      clearTimeout(navbarContext.hoverTimeoutId);
      navbarContext.setHoverTimeout(null);
    }

    const timeout = setTimeout(() => {
      navbarContext.setOpenItem(null);
    }, navbarItemContext.closeDelay);

    navbarContext.setCloseTimeout(timeout as unknown as number);
  };

  /**
   * Handles keydown events for keyboard navigation
   * @param {KeyboardEvent} event - The keydown event
   */
  const onkeydown = (event: KeyboardEvent) => {
    const eventTarget = event.target as HTMLElement;
    switch (event.key) {
      case 'Enter':
        handleEnter();
        break;
      case 'Escape':
        handleEscape();
        break;
      case 'ArrowDown':
        handleArrowDown(eventTarget);
        break;
      case 'ArrowUp':
        handleArrowUp(eventTarget);
        break;
    }
  };

  const handleEnter = () => {
    navbarContext.closeMenu();
    if (href) {
      goto(href);
    }
  };

  const handleEscape = () => {
    navbarContext.closeMenu();
    // Focuses the current parent item
    const item = document.querySelector(`[data-id="${navbarItemContext.id}"]`) as HTMLElement;
    item?.focus();
  };

  const handleArrowDown = (eventTarget: HTMLElement) => {
    const target =
      // Gets the next link
      (eventTarget.nextElementSibling as HTMLElement) ??
      // Gets the first link of the next itcolumnem
      (eventTarget.parentElement?.nextElementSibling?.children[0].children[0] as HTMLElement) ??
      // Gets the next navbar item
      (document?.querySelector(`[data-id="${navbarItemContext.id}"]`)?.parentElement
        ?.nextElementSibling?.children[0] as HTMLElement) ??
      undefined;
    if (target) {
      target.focus();
    }
  };

  const handleArrowUp = (eventTarget: HTMLElement) => {
    const previousTarget =
      // Focuses the previous link header
      (eventTarget.previousElementSibling?.children[0] as HTMLElement) ??
      // Focuses the previous link
      (eventTarget.previousElementSibling as HTMLElement) ??
      // Focuses the previous navbar item
      (document?.querySelector(`[data-id="${navbarItemContext.id}"]`)?.parentElement
        ?.previousElementSibling?.children[0] as HTMLElement) ??
      undefined;
    if (previousTarget) {
      previousTarget.focus();
    }
  };
</script>

<a
  {onfocus}
  {onblur}
  {onkeydown}
  {href}
  onclick={() => navbarContext.closeMenu()}
  class={css(styles.link, cssProp)}
  role="menuitem"
  aria-label={label}
>
  {label}
</a>
